import { FC, useCallback } from "react";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import AddIcon from "@mui/icons-material/Add";
import { IconButton } from "@mui/material";
import { useEffectOnceWhen } from "rooks";
import { useLocation } from "react-router-dom";
import { UniqueIdentifier } from "@dnd-kit/core";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";

import { DashboardV2NavigationAddActionMenu } from "./components/DashboardV2NavigationAddActionMenu";
import { DashboardV2ItemActionButton } from "./components/item-action-menu/DashboardV2ItemActionButton";
import { DashboardV2FolderActionButton } from "./components/folder-action-menu/DashboardV2FolderActionButton";
import { DashboardV2NavigationAddDashboardNavItem } from "./components/DashboardV2NavigationAddDashboardNavItem";
import { NavItem } from "../nav-item/NavItem";
import { SortableTree } from "../sortable-tree/SortableTree";
import {
  SortableTreeFolderActionArgs,
  SortableTreeItemActionArgs,
  SortableTreeOnchangeArgs,
} from "../sortable-tree/utils/types";
import { useAppAbility } from "../../../../services/permissions";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { navigationCollapsedSelector } from "../../../../store/common/selectors/navigation/navigationCollapsedSelector";
import { getDashboardsHierarchyThunk } from "../../../../store/dashboards/thunks/dashboard-hierarchy/getDashboardsHierarchyThunk";
import { useMenuHook } from "../../../../utils/hooks/useMenu.hook";
import { navigationDashboardDataSelector } from "../../../../store/navigation/selectors/navigationDashboardDataSelector";
import { updateDashboardHierarchyThunk } from "../../../../store/dashboards/thunks/dashboard-hierarchy/updateDashboardHierarchyThunk";
import { setNavigationDashboard } from "../../../../store/navigation/navigationSlice";

export const DashboardV2Navigation: FC = () => {
  const { anchor, openMenu, closeMenu, open } = useMenuHook();

  const dispatch = useAppDispatch();
  const { cannot, can } = useAppAbility();

  const location = useLocation();
  const selected = location.pathname.startsWith("/dashboards");

  const cannotCreateDashboard = cannot("create", "dashboard");
  const dashboardsAvailable = can("view", "dashboard");

  useEffectOnceWhen(() => {
    dispatch(getDashboardsHierarchyThunk());
  }, dashboardsAvailable);

  const navigationCollapsed = useAppSelector(navigationCollapsedSelector);

  const renderItemAction = useCallback(
    ({ id, name, parentId }: SortableTreeItemActionArgs) => {
      return (
        <DashboardV2ItemActionButton
          id={id}
          name={name}
          folderId={(parentId as string) ?? undefined}
        />
      );
    },
    [],
  );

  const hierarchyChangeHandler = useCallback(
    ({ index, id, parentId, items }: SortableTreeOnchangeArgs) => {
      if (!items) {
        return;
      }
      dispatch(setNavigationDashboard(items));
      dispatch(
        updateDashboardHierarchyThunk({
          id: id as string,
          index,
          folderId: (parentId as string) ?? undefined,
        }),
      );
    },
    [dispatch],
  );

  const hierarchyData = useAppSelector(navigationDashboardDataSelector);

  const navigateToHandler = useCallback((id: UniqueIdentifier) => {
    return `/dashboards/${id}`;
  }, []);

  const renderFolderAction = useCallback(
    ({ id, name }: SortableTreeFolderActionArgs) => {
      return <DashboardV2FolderActionButton id={id} name={name} />;
    },
    [],
  );

  const resetHandler = useCallback(() => {
    dispatch(setNavigationDashboard(null));
  }, [dispatch]);

  return (
    <NavItem
      primary="Dashboards"
      icon={DashboardCustomizeOutlinedIcon}
      selected={selected}
      secondaryAction={
        <IconButton
          size="small"
          disabled={cannotCreateDashboard}
          onClick={openMenu}
        >
          <AddIcon fontSize="inherit" />
        </IconButton>
      }
      navCollapsed={navigationCollapsed}
    >
      {hierarchyData && (
        <SortableTree
          items={hierarchyData}
          renderItemAction={renderItemAction}
          renderFolderAction={renderFolderAction}
          onChange={hierarchyChangeHandler}
          getNavigateTo={navigateToHandler}
          onReset={resetHandler}
          icon={GridViewOutlinedIcon}
          disabled={cannotCreateDashboard}
          emptyText="No dashboards inside"
        />
      )}

      <DashboardV2NavigationAddDashboardNavItem />
      <DashboardV2NavigationAddActionMenu
        open={open}
        onCloseMenu={closeMenu}
        anchorEl={anchor}
      />
    </NavItem>
  );
};
